<template>
    <div class="flex flex-col h-full pt-4">
        <div class="@container flex-container flex items-center pl-2 gap-2">
            <date-range-selector v-model="dateRange" :show-start-date-picker="!isOpen"/>
            <open-post-filter-selector v-model="isOpen"/>
            <general-input
                v-model="textQuery"
                label="Søk på poster"
                class="@[613px]:mt-0 mt-2"
                placeholder="Navn"
                autocomplete="off"
                icon="i-ph-magnifying-glass"
            />
        </div>
        <div class="flex flex-col items-end pr-11">
            <div class="text-sm text-base-content/50">Sum {{ isCustomer ? 'kundefordringer' : 'leverandørgjeld' }}</div>
            <div class="text-lg">{{ formatCurrency(ledgerTotalBalance) }}</div>
        </div>
        <div class="flex flex-col gap-2 overflow-y-auto bg-base-200 mr-3 ml-2">
            <dynamic-scroller
                :items="ledgerEntries"
                :min-item-size="72"
                key-field="subAccountUuid"
            >
                <template #default="{ item, index, active }">
                    <dynamic-scroller-item
                        :item="item"
                        :active="active"
                        :data-index="index"
                        :size-dependencies="[item.lineCount]"
                    >
                        <div :style="{height: subAccDetailsHeight(item) + 'px'}" class="py-0.5">
                            <sub-account-details
                                :ledger-entry="item"
                                :sub-account-filter="subAccountFilter"
                                class="bg-base-100 border-y-base-300"
                                :is-customer="isCustomer"
                                @refresh-ledger="fetchSubAccounts"
                            />
                        </div>
                    </dynamic-scroller-item>
                </template>
            </dynamic-scroller>
        </div>
    </div>
</template>

<script setup lang="ts">
//@ts-ignore
import {DynamicScroller, DynamicScrollerItem} from 'vue-virtual-scroller'
import SubAccountDetails from '@/subaccount/outstandingBalance/SubAccountDetails.vue'
import {DateRange} from '@/dateUtils/DateRange'
import {computed, onMounted, onUnmounted, ref, watch} from 'vue'
import {watchDebounced} from '@vueuse/core'
import {SubAccountLedgerFilter} from './SubAccountLedgerFilter'
import {defaultSearchParams} from '@/types/transaction/TransactionSearchParams'
import DateRangeSelector from '@/dateUtils/DateRangeSelector.vue'
import OpenPostFilterSelector from '@/subaccount/outstandingBalance/OpenPostFilterSelector.vue'
import {LedgerSubAccount} from '@/subaccount/types/SubAccountLedger'
import {DELETE_TRANSACTION, SACC_LINES_UPDATED, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import {useEmitter} from '@/services/infrastructure/emitter'
import {formatCurrency} from '@/services/formatter.service'
import GeneralInput from '@/components/GeneralInput.vue'
import {ORG_SELECTED} from '@/types/events'

const {onEvent, cleanUp} = useEmitter()


const defaultRange: DateRange = {
    start: new Date(defaultSearchParams().fromDate!),
    end: new Date(defaultSearchParams().toDate!)
}
const dateRange = ref<DateRange>(defaultRange)
const isOpen = ref(true)
const textQuery = ref('')
const ledgerEntries = ref<LedgerSubAccount[]>([])

const subAccDetailsHeight = (sacc: LedgerSubAccount) => {
    const lineCount = Math.min(sacc.lineCount, 31)
    return (lineCount * 29) + 125
}

const subAccountFilter = computed<SubAccountLedgerFilter>(() => {
    return {
        start: dateRange.value.start,
        end: dateRange.value.end,
        isOpen: isOpen.value,
        textQuery: textQuery.value
    }
})

const props = defineProps({
    subAccountGetter: {
        type: Function,
        required: true
    },
    isCustomer: {
        type: Boolean,
        required: true
    }
})

const ledgerTotalBalance = computed(() => {
    return ledgerEntries.value.reduce((balance, sacc) => balance + sacc.outgoing, 0)
})

const clear = () => {
    ledgerEntries.value = []
}

const fetchSubAccounts = async () => {
    const data = await props.subAccountGetter(subAccountFilter.value) as {entries: LedgerSubAccount[]}
    ledgerEntries.value = data.entries.filter(entry => entry.lineCount > 0)
}

onMounted(fetchSubAccounts)
watch([dateRange, isOpen], fetchSubAccounts)
watchDebounced([textQuery], fetchSubAccounts, {debounce:500})

onEvent(SACC_LINES_UPDATED, fetchSubAccounts)
onEvent(TRANSACTION_CREATED, fetchSubAccounts)
onEvent(TRANSACTION_UPDATED, fetchSubAccounts)
onEvent(DELETE_TRANSACTION, fetchSubAccounts)
onEvent(ORG_SELECTED, () => {
    clear()
    fetchSubAccounts()
})

onUnmounted(cleanUp)

</script>

<style scoped>

</style>